import React, { useState, useEffect, useCallback } from 'react';
import { inboxApi } from '../services/api';
import { useInbox } from '../context/InboxContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Inbox, Check, X, FileDown, Package, Eye, Printer } from 'lucide-react';
import { toast } from 'sonner';
import InboxDdtViewer from '../components/InboxDdtViewer';

const statusLabel = {
  pending: { label: 'In attesa', color: 'bg-amber-100 text-amber-800 border-amber-300' },
  accepted: { label: 'Accettato', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  rejected: { label: 'Rifiutato', color: 'bg-red-100 text-red-800 border-red-300' },
};

const InboxPage = () => {
  const { refresh: refreshBadge } = useInbox();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actingId, setActingId] = useState(null);
  const [viewEntry, setViewEntry] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await inboxApi.list();
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error('Errore nel caricamento inbox');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openPdf = async (entry) => {
    setDownloadingId(entry.id);
    try {
      const r = await inboxApi.pdfBlob(entry.id);
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      // Apre in nuovo tab: l'utente può visualizzare, stampare (Ctrl+P) e scaricare dalla toolbar del PDF.
      const w = window.open(url, '_blank', 'noopener,noreferrer');
      if (!w) {
        // Popup bloccato: fallback a download diretto
        const a = document.createElement('a');
        a.href = url;
        a.download = `DDT_${entry.ddt_snapshot?.number || 'ricevuto'}_${entry.ddt_snapshot?.year || ''}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      }
      // Libera l'URL quando il tab lo ha letto (dopo un po')
      setTimeout(() => window.URL.revokeObjectURL(url), 60 * 1000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel download del PDF');
    } finally {
      setDownloadingId(null);
    }
  };

  const accept = async (id) => {
    setActingId(id);
    try {
      const r = await inboxApi.accept(id);
      toast.success(`DDT accettato — creato DDT Acquisto n° ${r.data.number}/${r.data.year}`);
      load();
      refreshBadge();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'accettazione");
    } finally {
      setActingId(null);
    }
  };

  const reject = async (id) => {
    if (!window.confirm('Rifiutare questo DDT? L\'azione non è annullabile.')) return;
    setActingId(id);
    try {
      await inboxApi.reject(id);
      toast.success('DDT rifiutato');
      load();
      refreshBadge();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally {
      setActingId(null);
    }
  };

  const pending = items.filter(i => i.status === 'pending');
  const processed = items.filter(i => i.status !== 'pending');

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-6" data-testid="inbox-page">
      <div>
        <h1 className="text-2xl font-semibold font-['Manrope'] flex items-center gap-2">
          <Inbox className="w-6 h-6 text-blue-600" />
          Inbox DDT
          {pending.length > 0 && (
            <Badge className="bg-amber-500 text-white ml-2">{pending.length} in attesa</Badge>
          )}
        </h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          DDT ricevuti da altre aziende. Accettandone uno viene creato automaticamente un <b>DDT Acquisto</b> nel tuo sistema.
        </p>
      </div>

      <Card data-testid="inbox-pending-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Da approvare</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-6 text-center text-sm text-muted-foreground">Caricamento…</div>
          ) : pending.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              <Inbox className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">Nessun DDT in attesa.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Da</TableHead>
                  <TableHead>N° DDT</TableHead>
                  <TableHead>Articoli</TableHead>
                  <TableHead className="text-right">Totale</TableHead>
                  <TableHead className="text-right w-[220px]">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pending.map(it => (
                  <TableRow key={it.id} data-testid={`inbox-row-${it.id}`}>
                    <TableCell className="font-medium">{it.from_company_name}</TableCell>
                    <TableCell className="font-['JetBrains_Mono']">
                      {it.ddt_snapshot?.number}-{it.ddt_snapshot?.year}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      <span className="inline-flex items-center gap-1">
                        <Package className="w-3.5 h-3.5" />
                        {(it.ddt_snapshot?.items || []).length} prodotti · {Number(it.ddt_snapshot?.total_boxes || 0).toFixed(1)} box
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-['JetBrains_Mono'] font-semibold">
                      € {Number(it.ddt_snapshot?.total_amount || 0).toFixed(2)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1.5 justify-end flex-wrap">
                        <Button
                          data-testid={`inbox-view-${it.id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => setViewEntry(it)}
                          title="Visualizza DDT"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          data-testid={`inbox-pdf-${it.id}`}
                          size="sm"
                          variant="outline"
                          disabled={downloadingId === it.id}
                          onClick={() => openPdf(it)}
                          title="Apri PDF (visualizza / stampa / scarica)"
                        >
                          <FileDown className="w-4 h-4" />
                        </Button>
                        <Button
                          data-testid={`inbox-accept-${it.id}`}
                          size="sm"
                          disabled={actingId === it.id}
                          onClick={() => accept(it.id)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          <Check className="w-4 h-4 mr-1" /> Accetta
                        </Button>
                        <Button
                          data-testid={`inbox-reject-${it.id}`}
                          size="sm"
                          variant="outline"
                          disabled={actingId === it.id}
                          onClick={() => reject(it.id)}
                          className="text-destructive border-destructive/30 hover:bg-destructive/5"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {processed.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Processati di recente</CardTitle>
          </CardHeader>
          <CardContent>            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Da</TableHead>
                  <TableHead>N° DDT</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="text-right">Totale</TableHead>
                  <TableHead className="text-right w-[140px]">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {processed.map(it => (
                  <TableRow key={it.id} data-testid={`inbox-processed-${it.id}`}>
                    <TableCell className="font-medium">{it.from_company_name}</TableCell>
                    <TableCell className="font-['JetBrains_Mono']">
                      {it.ddt_snapshot?.number}-{it.ddt_snapshot?.year}
                    </TableCell>
                    <TableCell>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${statusLabel[it.status]?.color}`}>
                        {statusLabel[it.status]?.label || it.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-['JetBrains_Mono']">
                      € {Number(it.ddt_snapshot?.total_amount || 0).toFixed(2)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1.5 justify-end">
                        <Button
                          data-testid={`inbox-processed-view-${it.id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => setViewEntry(it)}
                          title="Visualizza"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          data-testid={`inbox-processed-pdf-${it.id}`}
                          size="sm"
                          variant="outline"
                          disabled={downloadingId === it.id}
                          onClick={() => openPdf(it)}
                          title="Apri PDF"
                        >
                          <Printer className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Modal anteprima read-only del DDT ricevuto */}
      <InboxDdtViewer
        open={!!viewEntry}
        entry={viewEntry}
        onClose={() => setViewEntry(null)}
      />
    </div>
  );
};

export default InboxPage;
